import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, TrendingUp, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";

function bandColor(v) {
    if (v == null) return "#94A3B8";
    if (v < 1.5) return "#DC2626";
    if (v < 2.5) return "#D97706";
    if (v < 3.5) return "#1E56A0";
    return "#059669";
}
function tintFor(v) {
    if (v == null) return "#F1F5F9";
    if (v < 1.5) return "#FEF2F2";
    if (v < 2.5) return "#FFF7ED";
    if (v < 3.5) return "#EEF4FB";
    return "#ECFDF5";
}

export default function LrfReflectionSummaryPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [trend, setTrend] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data: s } = await api.get(`/lrf/reflections/${id}/summary`);
                setSummary(s);
                try {
                    const { data: t } = await api.get(`/lrf/reflections/${id}/trend`);
                    setTrend(t);
                } catch {
                    setTrend({ cycles: [] });
                }
            } catch (e) {
                toast.error(extractError(e, "Could not load summary"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) {
        return (
            <div className="text-[#0B2545] inline-flex items-center gap-2" data-testid="summary-loading">
                <Loader2 className="animate-spin" size={16} /> Loading summary…
            </div>
        );
    }
    if (!summary) return null;

    const overall = summary.overall;
    const color = bandColor(overall);
    const tint = tintFor(overall);

    const radarData = summary.domain_stats.map((d) => ({
        domain: d.name.replace(/ &.*$/, ""),
        fullName: d.name,
        value: d.average == null ? 0 : Number(d.average.toFixed(2)),
    }));
    const trendData = (trend?.cycles || []).map((c) => ({
        label: c.period_label,
        overall: c.overall == null ? null : Number(c.overall.toFixed(2)),
        vision: c.domain_averages?.vision,
        leadership: c.domain_averages?.leadership,
        culture: c.domain_averages?.culture,
        curriculum: c.domain_averages?.curriculum,
        instructional: c.domain_averages?.instructional,
        data: c.domain_averages?.data,
    }));

    return (
        <div className="space-y-6" data-testid="lrf-summary">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to={`/app/lrf/reflections/${id}`} className="text-xs text-[#1E56A0] hover:underline">
                        ← Back to reflection
                    </Link>
                    <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545]">
                        Reflection Summary
                    </h1>
                    <p className="mt-2 text-sm text-[#0E1B2C]/70">
                        {summary.reflection.period_label} · {summary.reflection.status}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate(`/app/lrf/reflections/${id}`)}
                    className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                    data-testid="back-to-workspace-btn"
                >
                    Back to workspace <ArrowRight size={14} />
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5"
                    data-testid="lrf-overall-card"
                >
                    <div className="p-6" style={{ background: `linear-gradient(140deg, ${tint}, #FFFFFF 70%)` }}>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                            Overall Reflection Score
                        </div>
                        <div className="mt-2 flex items-end gap-3">
                            <div className="font-serif font-semibold leading-none" style={{ fontSize: "6rem", color }} data-testid="summary-overall-number">
                                {overall == null ? "—" : overall.toFixed(2)}
                            </div>
                            <div className="pb-3 text-sm text-[#0E1B2C]/70">/ 4.00</div>
                        </div>
                        <div className="mt-2 font-serif text-lg text-[#0B2545]" data-testid="summary-descriptor">
                            {summary.descriptor}
                        </div>
                    </div>
                </motion.div>

                <div className="lg:col-span-3 rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="summary-radar-card">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">Domain averages radar</div>
                    <div className="text-sm text-[#0E1B2C]/70 mt-1">Mean of the 6 objective ratings per domain (skip unrated).</div>
                    <div className="mt-4 h-[320px]" data-testid="summary-radar">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData} outerRadius="75%">
                                <PolarGrid stroke="#D6E6F2" />
                                <PolarAngleAxis dataKey="domain" tick={{ fill: "#0B2545", fontSize: 12, fontFamily: "Inter, sans-serif" }} />
                                <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: "#64748B", fontSize: 10 }} />
                                <Radar dataKey="value" stroke="#1E56A0" fill="#8AC4FF" fillOpacity={0.5} />
                                <Tooltip formatter={(v, _n, i) => [`${v}/4`, i?.payload?.fullName]} contentStyle={{ borderRadius: 8, border: "1px solid #D6E6F2" }} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="summary-domain-grid">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">By domain</div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {summary.domain_stats.map((d) => (
                        <div key={d.key} className="rounded-xl border border-[#D6E6F2] overflow-hidden" data-testid={`summary-domain-${d.key}`}>
                            <div className="h-1.5" style={{ backgroundColor: d.color_hex }} />
                            <div className="p-4 flex items-center gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: d.color_hex }}>
                                        Domain
                                    </div>
                                    <div className="font-serif text-lg font-semibold text-[#0B2545] truncate">{d.name}</div>
                                    <div className="text-xs text-[#0E1B2C]/60 mt-1">
                                        {d.total_objectives - d.unrated_objectives}/{d.total_objectives} objectives rated · {d.total_questions - d.empty_responses}/{d.total_questions} responses filled
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-serif text-3xl font-semibold" style={{ color: bandColor(d.average) }}>
                                        {d.average == null ? "—" : d.average.toFixed(2)}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E56A0]">/ 4.00</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="summary-trend-card">
                <div className="flex items-center gap-2">
                    <TrendingUp size={16} className="text-[#1E56A0]" />
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">Personal trend (completed reflections)</div>
                </div>
                {trendData.length === 0 ? (
                    <div className="mt-4 rounded-xl border border-dashed border-[#8AC4FF] bg-[#F1F6FB] px-4 py-8 text-center text-sm text-[#0E1B2C]/70">
                        <Sparkles className="mx-auto text-[#1E56A0]" size={18} />
                        <div className="mt-2">Complete more reflections over time to see a trend line.</div>
                    </div>
                ) : (
                    <div className="mt-4 h-[280px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5EEF7" />
                                <XAxis dataKey="label" stroke="#0B2545" fontSize={11} />
                                <YAxis domain={[0, 4]} stroke="#0B2545" fontSize={11} />
                                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #D6E6F2" }} />
                                <Legend />
                                <Line type="monotone" dataKey="overall" stroke="#0B2545" strokeWidth={3} dot={{ r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
